<!-- eslint-disable vue/no-deprecated-v-on-native-modifier -->
<template>
  <div class="mark-container" v-clickoutside="hideAllTippy">
    <EditorRender
      class="render-content" 
      ref="renderRef"
      :content="content"
      :interactionConfigs="interactionConfigs"
      :iframeConfigs="iframeConfigs"
      :appConfigs="appConfigs"
      :studentInfo="studentInfo"
      :defaultStyle="defaultStyle"
      :inductionIndex="inductionIndex"
      :inductionId="guid"
      :appList="appList"
      :nodeIdMap="nodeIdMap"
      @recordJump="data => $emit('recordJump', data)"
      @jumpTargetNode="$event => $emit('jumpTargetNode', $event)"
      @mouseup.native="handleSelectText()"
      @mousedown.native="$event => handleClick($event)"
      @contextmenu.native.prevent
    />

    <div class="selected-menu" :class="{ 'mobile': isMobile }" ref="selectedMenuRef">
      <div class="item colors">
        <div class="icon">
          <span class="color" 
            v-for="item in markColors" 
            :key="item.name"
            @click="createTextNote({ color: item.name })"
          >
            <span class="color-block" :style="{ backgroundColor: item.color }"></span>
          </span>
        </div>
        <div class="text">标注高亮</div>
      </div>
      <div class="item note" @click="createTextNote({})">
        <div class="icon">
          <IconFont type="icon-note" />
        </div>
        <div class="text">笔记</div>
      </div>
      <div class="item question" @click="createTextNote({ isQuestion: true })">
        <div class="icon">
          <IconFont type="icon-question" />
        </div>
        <div class="text">发布疑问</div>
      </div>
      <div class="item search" @click="search()">
        <div class="icon">
          <IconFont type="icon-search" />
        </div>
        <div class="text">搜索</div>
      </div>
      <div class="item card" @click="createCard()" v-if="classId === '0d2a6e80-b99e-11ee-a1d8-3f479df4d981' || classId === 'faa0a6d0-88d8-11ee-bd15-a3fe7f6ac316' || classId === 'e3ca3fa0-f7e8-11ee-88a6-7d54a331e71f'">
        <div class="icon">
          <IconFont type="icon-card" />
        </div>
        <div class="text">知识卡片</div>
      </div>
    </div>

    <div class="cards" ref="cardsRef">
      <template v-if="currentCard">
        <div class="header">
          <IconFont class="icon" type="icon-sound" @click="textToVoice(currentCard.text)" />
          <span class="text">{{ currentCard.text }}</span>
        </div>
        <div class="quote">{{ currentCard.quote }}</div>
        <div class="meaning">
          <textarea :value="currentCard.meaning" placeholder="输入释义" @blur="$event => updateCurrentCard({ meaning: $event.target.value })"></textarea>
        </div>
        <div class="btns">
          <div class="btn cancel" @click="hideCardsTippy()">取消</div>
          <div class="btn create" @click="submitCard()">创建卡片</div>
        </div>
      </template>
    </div>

    <div class="notes" ref="notesRef">
      <template v-if="currentNote && currentNote.noteType === 'question'">
        <div class="header" :style="{ backgroundColor: classNameMap[currentNote.className] }"></div>
        <textarea 
          class="textarea" 
          ref="questionTextareaRef"
          :value="currentNote.content" 
          v-if="currentNote.isUnfinish"
          placeholder="输入问题"
        ></textarea>
        <div class="question-preview" v-else>{{ currentNote.content }}</div>
        <div class="footer">
          <div class="left-menu">
            <IconFont 
              class="icon copy" 
              type="icon-copy" 
              v-if="currentNote.type === 'text' && !isMobile" 
              @click="copy(currentNote.text)"
              :style="{ color: classNameMap[currentNote.className] }"
              v-tooltip="'复制'"
            />
          </div>
          <div class="right-menu">
            <IconFont
              class="icon delete"
              type="icon-delete"
              @click="deleteNote(currentNote)"
              :style="{ color: classNameMap[currentNote.className] }"
              v-tooltip="'删除'"
            />
            <IconFont
              class="icon send"
              type="icon-send-fill"
              @click="createQuestion()"
              :style="{ color: classNameMap[currentNote.className] }"
              v-if="currentNote.isUnfinish"
              v-tooltip="'提交'"
            />
          </div>
        </div>
      </template>

      <template v-if="currentNote && currentNote.noteType !== 'question'">
        <div class="header" :style="{ backgroundColor: classNameMap[currentNote.className] }"></div>
        <div class="tools">
          <div class="left-menu">
              <IconFont 
                class="icon copy" 
                type="icon-copy" 
                v-if="currentNote.type === 'text' && !isMobile" 
                @click="copy(currentNote.text)"
                :style="{ color: classNameMap[currentNote.className] }"
                v-tooltip="'复制'"
              />
            <div class="color-item" 
              v-for="item in markColors" 
              :key="item.name" 
              :style="{ backgroundColor: item.color }"
              @click="updateColor(currentNote, item.name)"
            ></div>
          </div>
          <div class="right-menu">
            <IconFont 
              class="icon lock" 
              :type="currentNote.public ? 'icon-unlock-fill' : 'icon-lock-fill'"
              :style="{ color: classNameMap[currentNote.className] }"
              @click="setPublic(currentNote)"
              v-tooltip="currentNote.public ? '设为私密' : '公开笔记'"
            />
            <IconFont
              class="icon delete"
              type="icon-delete"
              @click="deleteNote(currentNote)"
              :style="{ color: classNameMap[currentNote.className] }"
              v-tooltip="'删除'"
            />
          </div>
        </div>
        <textarea 
          class="textarea" 
          :value="currentNote.content" 
          placeholder="输入笔记" 
          @input="$event => autoUpdateNoteContent(currentNote, $event.target.value)"
        ></textarea>
      </template>
    </div>

    <div class="ai-loading" @click.stop v-if="AILoading"></div>
  </div>
</template>

<script>
import tippy from 'tippy.js'
import debounce from 'lodash/debounce'
import Tooltip from './utils/tooltip'
import Clickoutside from './utils/clickoutside'
import Marker from './mark'
import { guid } from './mark/util'
import api from '@/services'

import EditorRender from './EditorRender'

import 'katex/dist/katex.min.css'
import 'viewerjs/dist/viewer.min.css'

import { message } from 'ant-design-vue'
import { createFromIconfontCN } from '@ant-design/icons-vue'

const IconFont = createFromIconfontCN({
  scriptUrl: '//at.alicdn.com/t/c/font_4416532_8c06zkjcak8.js',
})

const getTextWrapNode = el => {
  let targetNode = el
  while(!targetNode.dataset.id && targetNode.tagName !== 'BODY') {
    targetNode = targetNode.parentNode
  }
  if(targetNode.tagName === 'BODY') return null
  return targetNode
}

const getTopNode = nodeId => {
  const topNodes = document.querySelectorAll('.top-node')
  
  for(const node of topNodes) {
    if(node.dataset.id === nodeId) return node

    const targetNode = node.querySelector(`[data-id='${nodeId}']`)
    if(targetNode) return node
  }
  return null
}

const defaultSelectorClassName = 'selected-range'
const defaultSelectedBlockClassName = 'selected-block'

export default {
  name: 'mark-container',
  directives: {
    clickoutside: Clickoutside,
    tooltip: Tooltip,
  },
  components: {
    EditorRender,
    IconFont,
  },
  props: {
    content: {
      type: String,
      default: '',
    },
    interactionConfigs: {
      type: Object,
      default: () => ({}),
    },
    iframeConfigs: {
      type: Object,
      default: () => ({}),
    },
    appConfigs: {
      type: Object,
      default: () => ({}),
    },
    defaultStyle: {
      type: Object,
      default: () => ({
        fontSize: '16px',
      }),
    },
    inductionIndex: {
      type: Number,
      default: -1,
    },
    studentInfo: {
      type: Object,
      required: true,
    },
    guid: {
      type: String,
      required: true,
    },
    classId: {
      type: String,
      required: true,
    },
    appList: {
      type: Array,
      required: true,
    },
    nodeIdMap: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selection: null,
      currentNote: null,
      currentCard: null,
      selectedMenuTippy: null,
      crossParagraphTippy: null,
      notesTippy: null,
      cardsTippy: null,
      notes: [],
      cards: [],
      windowWidth: 0,
      initDataSuccess: false,
      marker: null,
      markColors: [
        { color: '#fed200', name: 'yellow' },
        { color: '#fc9399', name: 'pink' },
        { color: '#84cd01', name: 'green' },
        { color: '#00c3ed', name: 'blue' },
      ],
      classNameMap: {
        'selected-range': '#fed200',
        'selected-range-yellow': '#fed200',
        'selected-range-pink': '#fc9399',
        'selected-range-rose': '#de017e',
        'selected-range-blue': '#00c3ed',
        'selected-range-green': '#84cd01',
        'selected-range-purple': '#9f61fe',
        'question-selected-range-green': '#8cd7bd',
        'selected-block': '#fed200',
        'selected-block-yellow': '#fed200',
        'selected-block-pink': '#fc9399',
        'selected-block-rose': '#de017e',
        'selected-block-blue': '#00c3ed',
        'selected-block-green': '#84cd01',
        'selected-block-purple': '#9f61fe',
        'question-selected-block-green': '#8cd7bd',
      },
      prevSelection: '',
      soundPlaying: false,
      AILoading: false,
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth > 0 && this.windowWidth <= 700
    },
  },
  mounted() {
    this.windowWidth = document.body.clientWidth

    this.marker = new Marker({
      el: this.$refs.renderRef.$el,
      options: {
        isCover: true,
        ignoreClass: ['math-view', 'image-view'],
      },
    })
  
    this.initNotes()
    this.initCards()
    setTimeout(() => {
      this.initTippy()
      this.initTopNode()
      this.listenSelectionChange()
    }, 0)
  },
  beforeUnmount() {
    if(this.selectedMenuTippy) this.selectedMenuTippy.destroy()
    if(this.notesTippy) this.notesTippy.destroy()
    if(this.cardsTippy) this.cardsTippy.destroy()
  },
  watch: {
    notes: {
      handler() {
        if(!this.initDataSuccess) {
          this.initDataSuccess = true
        }
        else this.autoSave()
      },
      deep: true,
    },
  },
  methods: {
    listenSelectionChange() {
      if(!this.isMobile) return

      document.onselectionchange = () => {
        const currentSelection = window.getSelection().toString()

        if(currentSelection !== this.prevSelection) {
          this.handleSelectText()
        }
        this.prevSelection = currentSelection
      }
    },

    handleClick(e) {
      this.hideSelectedMenuTippy()
      this.hideCrossParagraphTippy()
      this.hideNotesTippy()
      this.hideCardsTippy()
      window.getSelection().removeAllRanges()

      if(e.target && e.target.dataset && e.target.dataset.selector) {
        const uid = e.target.dataset.selector
        const currentNote = this.notes.find(item => item.uid === uid)
        if(currentNote) {
          this.currentNote = currentNote
          this.openNotesTippy()
          return
        }
      }

      if(!e.target) return
      const isVideo = e.target.classList.contains('video-view')
      const isAudio = e.target.classList.contains('audio-view')
      const isIframe = e.target.classList.contains('iframe-view')

      if(isVideo || isAudio || isIframe) {
        const targetNode = e.target.querySelector(`[data-id]`)

        const nodeId = targetNode.dataset.id

        const selectedNote = this.notes.find(item => item.type === 'block' && item.nodeId === nodeId)

        if(selectedNote) {
          this.currentNote = selectedNote
          this.openNotesTippy()
        }
        else {
          let blockType = ''
          if(isVideo) blockType = 'video'
          else if(isAudio) blockType = 'audio'
          else if(isIframe) blockType = 'iframe'

          this.selection = {
            nodeId,
            type: 'block',
            blockType,
          }
          if(this.selectedMenuTippy) {
            this.selectedMenuTippy.setProps({
              getReferenceClientRect: () => e.target.getBoundingClientRect(),
            })
            this.selectedMenuTippy.show()
          }
        }
      }
    },

    handleSelectText: debounce(function() {
      this.selectText()
    }, 300, { trailing: true }),

    selectText() {
      const selection = window.getSelection()

      if(
        selection.isCollapsed ||
        !selection.anchorNode ||
        !selection.focusNode ||
        selection.type === 'Caret' ||
        selection.type === 'None'
      ) return

      const range = selection.getRangeAt(0)
      
      if(selection.anchorNode !== selection.focusNode) {
        const anchorAncestorTextNode = getTextWrapNode(selection.anchorNode.parentNode)
        const focusAncestorTextNode = getTextWrapNode(selection.focusNode.parentNode)

        if(anchorAncestorTextNode !== focusAncestorTextNode) {
          if(this.crossParagraphTippy) {
            this.crossParagraphTippy.setProps({
              getReferenceClientRect: () => range.getBoundingClientRect(),
            })
            this.crossParagraphTippy.show()
          }
          return
        }
      }

      const node = getTextWrapNode(selection.anchorNode.parentNode)

      const ret = this.marker.select()
      if(!ret) return
      
      if(!ret.hasStoreRender) {
        this.selection = {
          ...ret,
          nodeId: node.dataset.id,
          type: 'text',
        }
        if(this.selectedMenuTippy) {
          this.selectedMenuTippy.setProps({
            getReferenceClientRect: () => range.getBoundingClientRect(),
          })
          this.selectedMenuTippy.show()
        }
      }
    },

    createTextNote({ color, isQuestion }) {
      if(this.selection.type === 'text') {
        const selectorClassName = isQuestion ? 'question-selected-range-green' : (color ? `${defaultSelectorClassName}-${color}` : defaultSelectorClassName)
        this.selection.selection.removeAllRanges()
        this.selection.range.detach && this.selection.range.detach()
  
        const selectedRet = this.marker.repaintRange({
          uuid: this.selection.storeRenderOther.uid,
          textNodes: this.selection.textNodes,
          className: this.selection.storeRenderOther.className || selectorClassName,
        })
        this.currentNote = {
          type: 'text',
          nodeId: this.selection.nodeId,
          uid: selectedRet.uid,
          offset: this.selection.offset,
          relativeOffset: this.selection.relativeOffset,
          text: this.selection.text,
          className: selectorClassName,
          content: '',
          public: true,
          noteType: isQuestion ? 'question' : 'note',
        }
      }
      else if(this.selection.type === 'block') {
        const selectedBlockClassName = isQuestion ? 'question-selected-block-green' : (color ? `${defaultSelectedBlockClassName}-${color}` : defaultSelectedBlockClassName)
        this.repaintBlockRange(this.selection.nodeId, selectedBlockClassName)

        this.currentNote = {
          type: 'block',
          nodeId: this.selection.nodeId,
          uid: guid(),
          className: selectedBlockClassName,
          content: '',
          public: true,
          blockType: this.selection.blockType,
          noteType: isQuestion ? 'question' : 'note',
        }
      }
      if(isQuestion) this.currentNote.isUnfinish = true

      this.notes.push({
        ...this.currentNote,
        updateTime: new Date().toLocaleString(),
      })
      this.hideSelectedMenuTippy()

      if(!color) this.openNotesTippy()
    },

    deleteNote(note) {
      if(note.type === 'text') {
        this.marker.deleteMark(note.uid)
        this.notes = this.notes.filter(item => item.uid !== note.uid)
      }
      else if(note.type === 'block') {
        this.deleteBlockMark(note.nodeId, note.className)
        this.notes = this.notes.filter(item => item.nodeId !== note.nodeId)
      }

      this.hideNotesTippy()
    },

    deleteCard(uid) {
      this.marker.deleteMark(uid)
      this.cards = this.cards.filter(item => item.uid !== uid)

      this.hideCardsTippy()
    },

    updateCard(uid, props) {
      this.marker.deleteMark(uid)
      this.cards = this.cards.map(card => {
        if(card.uid === uid) {
          return {
            ...card,
            ...props,
          }
        }
        return card
      })

      this.hideCardsTippy()
    },

    createQuestion() {
      const content = this.$refs.questionTextareaRef.value

      if(!content) return

      if(this.currentNote.isUnfinish) {
        const newNote = {
          ...this.currentNote,
          content: content || '',
          updateTime: new Date().toLocaleString(),
        }
        delete newNote.isUnfinish
        this.currentNote = newNote

        if(this.currentNote.type === 'text') {
          this.notes = this.notes.map(item => {
            if(item.uid === this.currentNote.uid) return newNote
            return item
          })
        }
        else if(this.currentNote.type === 'block') {
          this.notes = this.notes.map(item => {
            if(item.nodeId === this.currentNote.nodeId) return newNote
            return item
          })
        }
      }
      this.hideNotesTippy()
    },

    updateColor(note, color) {
      if(note.type === 'text') {
        const className = `${defaultSelectorClassName}-${color}`
        this.marker.replaceMarkClass(note.uid, className)

        const newNote = { ...this.currentNote, className }
        this.currentNote = newNote
        this.notes = this.notes.map(item => {
          if(item.uid === note.uid) return newNote
          return item
        })
      }
      else if(note.type === 'block') {
        const className = `${defaultSelectedBlockClassName}-${color}`
        this.replaceBlockMarkClass(note.nodeId, note.className, className)

        const newNote = { ...this.currentNote, className }
        this.currentNote = newNote
        this.notes = this.notes.map(item => {
          if(item.nodeId === note.nodeId) return newNote
          return item
        })
      }
    },

    setPublic(note) {
      const newNote = {
        ...this.currentNote,
        public: !note.public,
      }
      this.currentNote = newNote

      if(note.type === 'text') {
        this.notes = this.notes.map(item => {
          if(item.uid === note.uid) return newNote
          return item
        })
      }
      else if(note.type === 'block') {
        this.notes = this.notes.map(item => {
          if(item.nodeId === note.nodeId) return newNote
          return item
        })
      }
    },

    updateNoteContent(note, content) {
      const newNote = {
        ...this.currentNote,
        content: content || '',
        updateTime: new Date().toLocaleString(),
      }
      this.currentNote = newNote

      if(note.type === 'text') {
        this.notes = this.notes.map(item => {
          if(item.uid === note.uid) return newNote
          return item
        })
      }
      else if(note.type === 'block') {
        this.notes = this.notes.map(item => {
          if(item.nodeId === note.nodeId) return newNote
          return item
        })
      }
    },

    autoUpdateNoteContent: debounce(function(note, content) {
      this.updateNoteContent(note, content)
    }, 1000, { trailing: true }),

    hideSelectedMenuTippy() {
      if(this.selectedMenuTippy) this.selectedMenuTippy.hide()
    },

    hideCrossParagraphTippy() {
      if(this.crossParagraphTippy) this.crossParagraphTippy.hide()
    },

    openNotesTippy() {
      let getReferenceClientRect = null
      if(this.currentNote.type === 'text') {
        const targetNodes = document.querySelectorAll(`[data-selector='${this.currentNote.uid}']`)
        if(targetNodes.length === 1) {
          const targetNode = targetNodes[0]
          getReferenceClientRect = () => targetNode.getBoundingClientRect()
        }
        else {
          const firstTargetNode = targetNodes[0]
          getReferenceClientRect = () => {
            const firstRect = firstTargetNode.getBoundingClientRect()
            let x = firstRect.x
            let y = firstRect.y
            let right = firstRect.right
            let bottom = firstRect.bottom
            for(let i = 1; i < targetNodes.length; i++) {
              const node = targetNodes[i]
              const rect = node.getBoundingClientRect()
              if(rect.x < x) x = rect.x
              if(rect.y < y) y = rect.y
              if(rect.right > right) right = rect.right
              if(rect.bottom > bottom) bottom = rect.bottom
            }
            return new DOMRect(x, y, right - x, bottom - y)
          }
        }
      }
      else if(this.currentNote.type === 'block') {
        const targetNode = getTopNode(this.currentNote.nodeId)
        getReferenceClientRect = () => targetNode.getBoundingClientRect()
      }

      this.hideSelectedMenuTippy()
      this.notesTippy.setProps({
        getReferenceClientRect,
      })
      this.notesTippy.show()
    },

    hideNotesTippy() {
      if(this.notesTippy) this.notesTippy.hide()
    },

    openCardsTippy() {
      let getReferenceClientRect = null
      const targetNodes = document.querySelectorAll(`[data-selector='${this.currentCard.uid}']`)
      if(targetNodes.length === 1) {
        const targetNode = targetNodes[0]
        getReferenceClientRect = () => targetNode.getBoundingClientRect()
      }
      else {
        const firstTargetNode = targetNodes[0]
        getReferenceClientRect = () => {
          const firstRect = firstTargetNode.getBoundingClientRect()
          let x = firstRect.x
          let y = firstRect.y
          let right = firstRect.right
          let bottom = firstRect.bottom
          for(let i = 1; i < targetNodes.length; i++) {
            const node = targetNodes[i]
            const rect = node.getBoundingClientRect()
            if(rect.x < x) x = rect.x
            if(rect.y < y) y = rect.y
            if(rect.right > right) right = rect.right
            if(rect.bottom > bottom) bottom = rect.bottom
          }
          return new DOMRect(x, y, right - x, bottom - y)
        }
      }

      this.hideSelectedMenuTippy()
      this.cardsTippy.setProps({
        getReferenceClientRect,
      })
      this.cardsTippy.show()
    },

    hideCardsTippy() {
      if(this.cardsTippy) this.cardsTippy.hide()
    },

    hideAllTippy() {
      this.hideSelectedMenuTippy()
      this.hideCrossParagraphTippy()
      this.hideNotesTippy()
      this.hideCardsTippy()
    },

    initTippy() {
      this.crossParagraphTippy = tippy(this.$refs.renderRef.$el, {
        duration: 0,
        content: '暂时不支持跨段落标注',
        theme: 'cross-paragraph',
        trigger: 'manual',
        placement: 'bottom-start',
        hideOnClick: 'toggle',
        offset: [0, 5],
      })
      this.selectedMenuTippy = tippy(this.$refs.renderRef.$el, {
        duration: 0,
        content: this.$refs.selectedMenuRef,
        interactive: true,
        trigger: 'manual',
        placement: this.isMobile ? 'top-end' : 'bottom-start',
        hideOnClick: 'toggle',
        offset: [0, 8],
      })
      this.notesTippy = tippy(this.$refs.renderRef.$el, {
        duration: 0,
        content: this.$refs.notesRef,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        offset: [0, 5],
        hideOnClick: 'toggle',
        zIndex: 10001,
        onHidden: () => {
          if(this.currentNote.noteType === 'question' && this.currentNote.isUnfinish) {
            this.deleteNote(this.currentNote)
          }
        },
      })
      this.cardsTippy = tippy(this.$refs.renderRef.$el, {
        duration: 0,
        content: this.$refs.cardsRef,
        interactive: true,
        trigger: 'manual',
        placement: 'bottom-start',
        offset: [0, 5],
        hideOnClick: 'toggle',
        zIndex: 10001,
        onHidden: () => {
          if(this.currentCard && this.currentCard.isUnfinish) {
            this.deleteCard(this.currentCard.uid)
          }
        },
        onShown: () => {
          this.AIGen()
        }
      })
    },

    initTopNode() {
      const topNodes = document.querySelectorAll('.render-content > *')
      for(const node of topNodes) {
        node.classList.add('top-node')
      }
    },

    async initNotes() {
      const ret = await api.note.getNotes({
        trainId: this.guid,
        studentId: this.studentInfo.PK_UserGuid,
        classId: this.classId,
      })
      if(ret.data && ret.data.notes) {
        this.notes = ret.data.notes
        const textNotes = this.notes.filter(item => item.type === 'text')
        textNotes.length && this.marker.renderStore(textNotes)

        const blockNotes = this.notes.filter(item => item.type === 'block')
        for(const blockNode of blockNotes) {
          this.repaintBlockRange(blockNode.nodeId, blockNode.className)
        }
      }
      else this.initDataSuccess = true
    },

    async initCards() {
      const ret = await api.card.getCards({
        trainId: this.guid,
        studentId: this.studentInfo.PK_UserGuid,
        classId: this.classId,
      })
      if(ret.data) {
        this.cards = ret.data
        this.cards.length && this.marker.renderStore(this.cards)
      }
    },

    repaintBlockRange(nodeId, className) {
      const topNode = getTopNode(nodeId)
      topNode.classList.add(className)
    },

    replaceBlockMarkClass(nodeId, oldClassName, className) {
      const topNode = getTopNode(nodeId)
      topNode.classList.remove(oldClassName)
      topNode.classList.add(className)
    },

    deleteBlockMark(nodeId, className) {
      const topNode = getTopNode(nodeId)
      topNode.classList.remove(className)
    },

    saveNotes() {
      const notes = this.notes.filter(item => !item.isUnfinish).map(item => {
        if(item.isUnfinish !== undefined) delete item.isUnfinish
        return item
      })

      api.note.createOrUpdate({
        fk_trainId: this.guid,
        fk_studentId: this.studentInfo.PK_UserGuid,
        fk_classId: this.classId,
        notes,
      }).then(() => {
        this.$emit('noteUpdated')
      })
    },

    autoSave: debounce(function() {
      this.saveNotes()
    }, 500, { trailing: true }),

    copy(text) {
      navigator.clipboard.writeText(text)
      message.success('已复制到剪贴板')
    },

    search() {
      this.$emit('search', this.selection.text)
      this.hideSelectedMenuTippy()
    },

    textToVoice(text) {
      if(this.soundPlaying) return
      api.tts.TTS(text).then(audio => {
        if(audio) {
          const audioNode = new Audio()
          audioNode.src = audio
          audioNode.play()

          this.soundPlaying = true
          audioNode.addEventListener('ended', () => this.soundPlaying = false)
        }
      })
    },

    updateCurrentCard(props) {
      this.currentCard = {
        ...this.currentCard,
        ...props,
      }
    },

    AIGen() {
      if(!this.currentCard) return
      this.AILoading = true
      api.unifydata.getChat({
        messages: [
          { 
            role: 'system', 
            content: `我希望你将我提供的英文单词、词组、句子进行释义，例如我给你“hello”，你应该回复我“int./n. 你好;(用于问候、接电话或引起注意)喂;(表示惊讶或认为别人说了蠢话或没有注意听)嘿”。注意回答中不要包含问题中的文字内容，不要用引号括住回复。`,
          },
          {
            role: 'user', 
            content: `请给出以下内容的释义：“${this.currentCard.text}”`
          },
        ]
      }).then(ret => {
        this.AILoading = false
        try {
          const content = ret.data[0].content.replace(/\n/g, ' ')
          this.updateCurrentCard({ meaning: content })
        }
        catch {
          message.error('AI执行失败，请重试')
        }        
      }).catch(() => {
        this.AILoading = false
        message.error('AI执行失败，请重试')
      })
    },

    submitCard() {
      if(!this.currentCard) return
      if(this.currentCard.isUnfinish !== undefined) delete this.currentCard.isUnfinish

      if(!this.currentCard.meaning) {
        return message.error('请先将卡片信息补充完整')
      }

      api.card.createCard({
        ...this.currentCard,
        trainId: this.guid,
        studentId: this.studentInfo.PK_UserGuid,
        classId: this.classId,
      }).then(() => {
        this.$emit('cardUpdated')
        this.hideCardsTippy()
      })
    },

    createCard() {
      this.selection.selection.removeAllRanges()
      this.selection.range.detach && this.selection.range.detach()
  
      const selectedRet = this.marker.repaintRange({
        uuid: this.selection.storeRenderOther.uid,
        textNodes: this.selection.textNodes,
        className: this.selection.storeRenderOther.className || 'card-selected-range',
      })

      const block = document.querySelector(`[data-id='${this.selection.nodeId}']`)

      const quotes = block.innerText.replace(/[“”‘’"']/g, '').split(/(?<=[.!?])/)
      let quote = ''
      for(const item of quotes) {
        if(item.indexOf(this.selection.text) !== -1) {
          quote = item
          break
        }
      }

      this.currentCard = {
        nodeId: this.selection.nodeId,
        uid: selectedRet.uid,
        offset: this.selection.offset,
        relativeOffset: this.selection.relativeOffset,
        text: this.selection.text,
        quote,
        meaning: '',
        state: 1,
        lastflag: false,
        isUnfinish: true,
      }
      this.hideSelectedMenuTippy()
      this.openCardsTippy()
    },
  },
}
</script>

<style lang="scss" scoped>
.notes {
  width: 350px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.15);
  overflow: hidden;

  .header {
    height: 8px;
    background-color: #fed200;
  }
  
  .textarea {
    width: 100%;
    height: 150px;
    outline: 0;
    border: 0;
    font-size: 13px;
    line-height: 1.5;
    padding: 8px;
    resize: none;

    &::placeholder {
      color: #999;
    }
  }
  .question-preview {
    width: 100%;
    height: 150px;
    font-size: 13px;
    line-height: 1.5;
    padding: 8px;
  }
  .tools {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    background-color: #f9f9f9;
    font-size: 16px;
    
    .btn {
      height: 22px;
      font-size: 12px;
      background-color: #fff;
      color: #999;
      margin-left: 5px;
      border: 1px solid #ddd;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
      cursor: pointer;
    }
    .icon {
      color: #888;
      cursor: pointer;
    }
  }
  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    font-size: 16px;

    .btn {
      font-size: 12px;
      cursor: pointer;
    }
  }
  .left-menu {
    display: flex;
    align-items: center;

    .copy {
      margin-right: 10px;
    }

    .color-item {
      width: 18px;
      height: 18px;
      margin-right: 5px;
      border-radius: 50%;
      opacity: .3;
      cursor: pointer;
    }
  }
  .right-menu {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .icon.delete {
      margin-left: 10px;
    }

    .icon.send {
      margin-left: 10px;
    }
  }
}
.cards {
  width: 280px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.15);
  overflow: hidden;
  padding: 15px;

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .icon {
      margin-right: 5px;
      font-size: 20px;
      color: #666;
      cursor: pointer;
    }
    .text {
      font-weight: 700;
      font-size: 18px;
    }
  }
  .quote {
    background-color: #eee;
    color: #777;
    border-radius: 10px;
    padding: 8px;
    font-size: 13px;
  }
  .meaning {
    height: 80px;
    margin-top: 3px;

    textarea {
      width: 100%;
      height: 100%;
      outline: 0;
      border: 0;
      font-size: 13px;
      line-height: 1.5;
      padding: 8px;
      color: #555;
      resize: none;
  
      &::placeholder {
        color: #999;
      }
    }
  }
  .btns {
    display: flex;
    justify-content: center;
    margin-top: 15px;

    .btn {
      width: 90px;
      padding: 4px 0;
      font-size: 13px;
      text-align: center;
      border-radius: 6px;
      cursor: pointer;

      &.cancel {
        background-color: #eee;
        color: #333;
        margin-right: 15px;
      }
      &.create {
        background-color: #f98d99;
        color: #fff;
      }
    }
  }
}
.selected-menu {
  display: flex;
  background-color: #fff;
  padding: 5px 10px;
  border-radius: 6px;
  box-shadow: 4px 4px 20px 0 rgba(0, 0, 0, .2);

  .item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    &:not(.colors) {
      cursor: pointer;
    }

    & + .item {
      margin-left: 10px;
    }

    &.colors {
      margin-right: 3px;

      .color {
        width: 28px;
        height: 28px;
        display: flex;
        justify-content: center;
        align-items: center;
        display: flex;
        align-items: center;

        &:hover .color-block {
          transform: scale(1.1);
          transition: transform .2s;
        }
      }
      .icon {
        display: flex;
        justify-content: center;
        align-items: center;

        .color-block {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          opacity: .3;
          cursor: pointer;
        }
      }
    }
  }
  .icon {
    height: 32px;
    font-size: 20px;
    color: #444;
  }
  .text {
    font-size: 12px;
    color: #000;
  }
}
.ai-loading {
  position: fixed;
  inset: 0;
  z-index: 999999;
  background-color: rgba($color: #000, $alpha: .15);

  &::before {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #e86b99;
    border-top-color: transparent;
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: loading 1s linear infinite;
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: -16px;
    margin-top: -48px;
    z-index: 1;
  }

  &::after {
    content: 'AI生成中，请稍等';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding-top: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    color: #e86b99;
  }
}
@keyframes loading {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
.selected-range, [data-selector] {
  background-color: rgba($color: #fed200, $alpha: .25);
  cursor: pointer;
}
.selected-range-yellow {
  background-color: rgba($color: #fed200, $alpha: .25);
  cursor: pointer;
}
.selected-range-pink {
  background-color: rgba($color: #fc9399, $alpha: .25);
  cursor: pointer;
}
.selected-range-rose {
  background-color: rgba($color: #de017e, $alpha: .25);
  cursor: pointer;
}
.selected-range-blue {
  background-color: rgba($color: #00c3ed, $alpha: .25);
  cursor: pointer;
}
.selected-range-green {
  background-color: rgba($color: #84cd01, $alpha: .25);
  cursor: pointer;
}
.selected-range-purple {
  background-color: rgba($color: #9f61fe, $alpha: .25);
  cursor: pointer;
}
.question-selected-range-green {
  background-color: rgba($color: #8cd7bd, $alpha: .25);
  cursor: pointer;
}

@mixin selected-block {
  content: '';
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: -2px;
  right: -2px;
  border-radius: 10px;
  pointer-events: none;
}
.selected-block {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #fed200, $alpha: .25);
  }
}
.selected-block-yellow {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #fed200, $alpha: .25);
  }
}
.selected-block-pink {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #fc9399, $alpha: .25);
  }
}
.selected-block-rose {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #de017e, $alpha: .25);
  }
}
.selected-block-blue {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #00c3ed, $alpha: .25);
  }
}
.selected-block-green {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #84cd01, $alpha: .25);
  }
}
.selected-block-purple {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #9f61fe, $alpha: .25);
  }
}
.question-selected-block-green {
  position: relative;
  &::after {
    @include selected-block();
    border: 2px dashed rgba($color: #8cd7bd, $alpha: .25);
  }
}

.tippy-box[data-theme~='cross-paragraph'] {
  height: 32px;
  line-height: 32px;
  background-color: #fff;
  color: #666;
  font-style: italic;
  padding: 0 10px;
  border-radius: 6px;
  box-shadow: 4px 4px 20px 0 rgba(0, 0, 0, .2);
}
</style>